import React, { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import Badge from "components/atoms/Badge/Badge";
import Button from "components/atoms/Button/Button";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import DataTable from "components/organisms/DataTable/DataTable";
import usePermissions from "hooks/usePermissions";
import useTableRequest from "hooks/useTableRequest";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import { getInvoiceAttachmentBuckets } from "api";
import { FormProvider, useForm } from "react-hook-form";
import AttachmentBucketModal from "./AttachmentBucketModal";

function InvoiceAttachmentBucket() {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const adminSettings = JSON.parse(permissions || "{}")?.permissions?.admin_settings;
  const methods = useForm();
  const { reset, clearErrors, setValue } = methods;
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState<any>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [show, setShow] = useState(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedBucket, setSelectedBucket] = useState<any>({});

  const columns = useMemo(
    () => [
      {
        Header: <FormattedMessage id="general.status" defaultMessage="Status" />,
        accessor: "is_active",
        style: { width: "100px" },
        Cell: ({ value }: any) => {
          const badgeType = (type: string) => {
            if (type === "Inactive") return "secondary";
            return "primary";
          };
          return (
            <div className="d-flex">
              <Badge
                type={badgeType(value ? "Active" : "Inactive")}
                label={
                  value
                    ? intl.formatMessage({
                        id: "general.active",
                        defaultMessage: "Active",
                      })
                    : intl.formatMessage({
                        id: "general.inActive",
                        defaultMessage: "Inactive",
                      })
                }
              />
            </div>
          );
        },
      },
      {
        Header: <FormattedMessage id="general.name" defaultMessage="Name" />,
        accessor: "bucket_name",
        sortable: true,
        Cell: ({ value }: any) => <p className="mb-0">{value}</p>,
      },
    ],
    [],
  );

  const { instance, fetching, totalData, refetch } = useTableRequest(
    () =>
      getInvoiceAttachmentBuckets({
        params: {
          all_attachments: true,
          page_size: instance?.state?.pageSize,
          page: instance?.state?.pageIndex + 1,
          ...(category && { status: category }),
          ...(searchQuery && { query: searchQuery }),
          ...(instance?.state?.sortBy?.[0] && {
            sort_by: instance?.state?.sortBy?.[0]?.desc ? "name:DESC" : "name:ASC",
          }),
        },
      }),
    columns,
    {
      params: {},
      filters: (globalFilter) => ({
        ...(globalFilter.query?.length && { query: globalFilter.query }),
      }),
      deps: [category, searchQuery],
    },
  );

  function openNewAttachmentBucket() {
    setSelectedBucket({});
    setIsEdit(false);
    setValue("is_active", "Active");
    setShow(true);
  }

  function handleClick(value: any) {
    setSelectedBucket({ ...value });
    setIsEdit(true);
    setShow(true);
  }

  const onClose = () => {
    setSelectedBucket({});
    setShow(false);
    setIsEdit(false);
    reset();
    clearErrors();
  };

  return (
    <>
      <FormProvider {...methods}>
        <AttachmentBucketModal
          isOpen={show}
          close={onClose}
          isEdit={isEdit}
          selectedBucket={selectedBucket}
          refetch={refetch}
        />
      </FormProvider>

      <DataTable
        tableInstance={instance}
        totalData={totalData}
        isLoading={fetching}
        draggable={false}
        onRowClick={(row) => {
          handleClick(row.original);
        }}
        headerLeft={
          <div className="d-md-flex justify-content-between flex-md-row flex-column w-100">
            <div className="d-flex  align-items-center mt-3 ">
              <PillTabs
                id="invoice-attachment-statuses"
                activeKey={category}
                onSelect={(eventKey: any) => {
                  searchParams.set("page", "1");
                  setSearchParams(searchParams);
                  setCategory(eventKey);
                }}
                tabItems={[
                  {
                    eventKey: "",
                    title: intl.formatMessage({
                      id: "settings.invoice-attachment.showAll",
                      defaultMessage: "Show all",
                    }),
                  },
                  {
                    eventKey: "active",
                    title: intl.formatMessage({
                      id: "general.active",
                      defaultMessage: "Active",
                    }),
                  },
                  {
                    eventKey: "inactive",
                    title: intl.formatMessage({
                      id: "general.inActive",
                      defaultMessage: "Inactive",
                    }),
                  },
                ]}
              />
              <div className="ml-3 ml-md-0">
                <GlobalTableSearch
                  currentFilters={instance.state.globalFilter}
                  setFilters={(filters: any) => {
                    instance.setGlobalFilter(filters);
                    setSearchQuery(filters.query);
                  }}
                  filterKey="query"
                  eventContext="Invoice Attachment Bucket"
                />
              </div>
            </div>

            <div className="d-flex align-items-center mt-3">
              {adminSettings?.edit_attachment_buckets && (
                <Button
                  label={intl.formatMessage({
                    id: "settings.invoice-attachment.newAttachmentBucket",
                    defaultMessage: "New attachment bucket",
                  })}
                  className="rounded-pill ml-3"
                  style={{ height: "26px" }}
                  onClick={openNewAttachmentBucket}
                />
              )}
            </div>
          </div>
        }
      />
    </>
  );
}

InvoiceAttachmentBucket.propTypes = {
  globalFilter: PropTypes.string,
  setGlobalFilter: PropTypes.func,
  value: PropTypes.string,
  columns: PropTypes.instanceOf(Array).isRequired,
};

InvoiceAttachmentBucket.defaultProps = {
  globalFilter: "",
  setGlobalFilter: null,
  value: "",
};

export default InvoiceAttachmentBucket;
